export default function ComparisonChecklist() {
  const questions = [
    {
      question: 'Are you licensed and insured in Texas?',
      why: 'Texas requires roofing contractors to be registered with the Texas Department of Licensing and Regulation. Verify their license number and ensure they carry both liability insurance and workers\' compensation. This protects you from liability if accidents occur on your property.',
      redFlag: 'Contractors who hesitate to provide license numbers or insurance certificates should be avoided.'
    },
    {
      question: 'How long have you been roofing in El Paso specifically?',
      why: 'El Paso\'s desert climate requires specialized knowledge. Contractors with local experience understand thermal expansion issues, proper ventilation for extreme heat, wind uplift requirements, and which materials perform best in our UV-intense environment.',
      redFlag: 'Be cautious of contractors new to the area who may not understand local building codes and climate challenges.'
    },
    {
      question: 'Can you provide local references from the past year?',
      why: 'Recent references in your neighborhood demonstrate current work quality and customer satisfaction. Ask to see completed projects similar to yours. Drive by reference addresses to inspect roof quality firsthand.',
      redFlag: 'Contractors who only provide old references or can\'t show local work may lack recent experience or satisfied customers.'
    },
    {
      question: 'What roofing materials do you recommend for my home and why?',
      why: 'Quality contractors will assess your home\'s architecture, structural capacity, and your budget before recommending materials. They should explain pros and cons of different options specific to El Paso\'s climate, not just push one product.',
      redFlag: 'Beware of contractors who immediately recommend the most expensive option without discussing alternatives or your specific needs.'
    },
    {
      question: 'Will you obtain all necessary permits?',
      why: 'Roofing work in El Paso requires building permits. Reputable contractors handle all permitting and schedule required inspections. Permits ensure work meets code and protects your home\'s resale value.',
      redFlag: 'Contractors who suggest skipping permits to save money are breaking the law and putting you at risk.'
    },
    {
      question: 'What is your project timeline and payment schedule?',
      why: 'Professional contractors provide written timelines with start and completion dates. Payment should be structured with a reasonable deposit (typically 10-25%), progress payments tied to milestones, and final payment upon completion and your satisfaction.',
      redFlag: 'Never pay the full amount upfront. Contractors demanding large deposits or cash-only payments are major red flags.'
    },
    {
      question: 'What warranties do you offer on materials and workmanship?',
      why: 'Quality roofing includes both manufacturer material warranties (20-50 years) and contractor workmanship warranties (minimum 5-10 years). Get all warranty terms in writing, including what\'s covered and exclusions.',
      redFlag: 'Contractors offering no workmanship warranty or vague warranty terms lack confidence in their work quality.'
    },
    {
      question: 'How will you protect my property during the project?',
      why: 'Professional contractors use tarps to protect landscaping, magnetic rollers to collect nails, and proper disposal methods for old materials. They should explain their cleanup process and debris removal procedures.',
      redFlag: 'Contractors who don\'t discuss property protection may leave you with damaged landscaping and safety hazards.'
    },
    {
      question: 'Do you have experience with insurance claims for storm damage?',
      why: 'If filing an insurance claim for hail or wind damage, experienced contractors can document damage, work with adjusters, and ensure you receive fair compensation. They understand insurance requirements and proper claim procedures.',
      redFlag: 'Avoid contractors who offer to waive your deductible or inflate claims—this is insurance fraud.'
    },
    {
      question: 'Can you provide a detailed written estimate?',
      why: 'Professional estimates itemize all costs including materials (brand and grade), labor, permits, disposal, and any additional work needed. Written estimates prevent surprise charges and allow accurate comparison between contractors.',
      redFlag: 'Verbal estimates or vague quotes that don\'t specify materials and scope of work often lead to disputes and unexpected costs.'
    }
  ];

  return (
    <section id="checklist" className="py-20 px-6 bg-gray-50">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            10 Questions to Ask Your El Paso Roofer
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Use this comprehensive checklist when interviewing roofing contractors. These critical questions help you identify qualified professionals and avoid costly mistakes. Print this list and bring it to every consultation.
          </p>
        </div>

        <div className="bg-white rounded-xl border-2 border-teal-600 p-8 mb-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-lg flex-shrink-0">
              <i className="ri-lightbulb-line text-2xl text-teal-600"></i>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">How to Use This Checklist</h3>
              <p className="text-sm text-gray-700 leading-relaxed">
                Schedule consultations with at least 3 contractors. Ask each question and take notes on their responses. Pay attention not just to what they say, but how they communicate—professional contractors will answer confidently and thoroughly. Compare responses to identify the most qualified contractor for your project.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {questions.map((item, index) => (
            <div key={index} className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
              <div className="p-6">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-teal-600 text-white font-bold rounded-lg flex-shrink-0">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900 mb-3">{item.question}</h3>
                    
                    <div className="space-y-3">
                      <div className="bg-blue-50 rounded-lg p-4">
                        <p className="text-xs font-semibold text-blue-900 mb-1 flex items-center gap-2">
                          <i className="ri-information-line"></i>
                          Why This Matters
                        </p>
                        <p className="text-sm text-blue-900 leading-relaxed">{item.why}</p>
                      </div>
                      
                      <div className="bg-red-50 rounded-lg p-4">
                        <p className="text-xs font-semibold text-red-900 mb-1 flex items-center gap-2">
                          <i className="ri-alert-line"></i>
                          Red Flag Warning
                        </p>
                        <p className="text-sm text-red-900 leading-relaxed">{item.redFlag}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 bg-gradient-to-r from-gray-900 to-gray-800 rounded-xl p-8">
          <h3 className="text-2xl font-bold text-white mb-4 text-center">
            Additional Tips for Hiring Roofing Contractors
          </h3>
          <div className="grid md:grid-cols-2 gap-6 text-sm text-white/90">
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-teal-400 text-xl flex-shrink-0 mt-0.5"></i>
                <p><strong className="text-white">Get Multiple Bids:</strong> Obtain at least 3 written estimates to compare pricing, materials, and approaches. The lowest bid isn\'t always the best value.</p>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-teal-400 text-xl flex-shrink-0 mt-0.5"></i>
                <p><strong className="text-white">Verify Online Reviews:</strong> Check Google, BBB, and Yelp for consistent positive feedback. Look for how contractors respond to negative reviews.</p>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-teal-400 text-xl flex-shrink-0 mt-0.5"></i>
                <p><strong className="text-white">Review the Contract Carefully:</strong> Ensure everything discussed is in writing, including materials, timeline, payment schedule, and warranty terms.</p>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-teal-400 text-xl flex-shrink-0 mt-0.5"></i>
                <p><strong className="text-white">Avoid Storm Chasers:</strong> After hail events, out-of-state contractors flood El Paso. Stick with established local companies who will be here for warranty work.</p>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-teal-400 text-xl flex-shrink-0 mt-0.5"></i>
                <p><strong className="text-white">Trust Your Instincts:</strong> If something feels off or a contractor pressures you to sign immediately, walk away. Quality contractors respect your decision-making process.</p>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-teal-400 text-xl flex-shrink-0 mt-0.5"></i>
                <p><strong className="text-white">Document Everything:</strong> Take photos before, during, and after the project. Keep all receipts, permits, and warranty documents in a safe place.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
